<template>
    <v-container fluid v-if="finding" :alvqa="alvqa">
        <v-row class="pl-4 pr-4" align="center">
            <v-col class="pb-0">
                <span class="title">{{ $t('lang_PUBLISHING_USAGE_CAPTION_REASON_' + finding.reason.toUpperCase()) }}</span>
                <OutcomeChip :outcome="finding.outcome" class="ml-2" :alvqa="alvqa" />
                <span class="ml-3 caption subtitleGray--text" :alvqa="alvqa + '-reference'">{{ finding.reference }}</span>
            </v-col>
        </v-row>

<!-- SIDE BY SIDE: the schedule's airing and what the society reported -->
        <v-row class="pl-4 pr-4">
            <v-col cols="12" md="5">
                <div class="subtitle-1 font-weight-medium mb-1">{{ $t('lang_PUBLISHING_USAGE_CAPTION_BROADCAST_SCHEDULE') }}</div>
                <v-simple-table dense class="finding-table usage-nested-table" :alvqa="alvqa + '-airing'">
                    <tbody>
                        <tr><td class="subtitleGray--text">{{ $t('lang_PUBLISHING_USAGE_CAPTION_AIRING') }}</td><td>{{ date(airing.date) }} {{ airing.startTime }}<span v-if="airing.durationSeconds"> · {{ duration(airing.durationSeconds) }}</span></td></tr>
                        <tr><td class="subtitleGray--text">{{ $t('lang_PUBLISHING_USAGE_CAPTION_STATION') }}</td><td>{{ airing.stationName }}<span v-if="airing.stationCode" class="subtitleGray--text"> ({{ airing.stationCode }})</span></td></tr>
                        <tr><td class="subtitleGray--text">{{ $t('lang_PUBLISHING_USAGE_CAPTION_PROGRAMME') }}</td><td>{{ airing.seriesTitle }}</td></tr>
                        <tr><td class="subtitleGray--text">{{ $t('lang_PUBLISHING_SHEETS_DROPDOWN_AVTYPE_EPISODE') }}</td><td>{{ airing.episodeTitle }}<span v-if="airing.episode" class="subtitleGray--text"> · {{ airing.episode }}</span></td></tr>
                        <tr v-if="airing.isRepeat"><td class="subtitleGray--text">{{ $t('lang_PUBLISHING_USAGE_CAPTION_FINDING_REPEAT') }}</td><td>✓</td></tr>
                        <tr v-if="finding.work">
                            <td class="subtitleGray--text">{{ $t(finding.workSource === 'statement' ? 'lang_PUBLISHING_USAGE_CAPTION_FINDING_STATEMENT_WORK' : 'lang_PUBLISHING_USAGE_CAPTION_FINDING_SHEET_WORK') }}</td>
                            <td>
                                {{ finding.work.title }} <span class="subtitleGray--text">{{ finding.work.gemaWorkNumber || $t('lang_PUBLISHING_USAGE_CAPTION_FINDING_NO_GEMA_NUMBER') }}</span>
                                <span v-if="finding.work.musicSeconds"> · {{ duration(finding.work.musicSeconds) }}</span>
                            </td>
                        </tr>
                        <tr v-else-if="finding.workSource === 'airing'">
                            <td colspan="2" class="subtitleGray--text" :alvqa="alvqa + '-no-work'">{{ $t('lang_PUBLISHING_USAGE_CAPTION_FINDING_NO_WORK') }}</td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </v-col>

            <v-col cols="12" md="7">
                <div class="subtitle-1 font-weight-medium mb-1">{{ $t('lang_PUBLISHING_USAGE_CAPTION_USAGE_STATEMENT') }}</div>
                <v-alert v-if="!finding.reported.length" color="standardBrownIcon" icon="warning_amber" dense border="left" colored-border class="mb-0" :alvqa="alvqa + '-not-reported'">
                    {{ $t('lang_PUBLISHING_USAGE_CAPTION_REASON_AIRING_UNREPORTED') }}
                </v-alert>
                <v-simple-table v-else dense class="finding-table usage-nested-table" :alvqa="alvqa + '-reported'">
                    <thead>
                        <tr>
                            <th>{{ $t('lang_PUBLISHING_USAGE_CAPTION_AIRING') }}</th>
                            <th>{{ $t('lang_PUBLISHING_USAGE_CAPTION_STATION') }}</th>
                            <th>{{ $t('lang_PUBLISHING_SHEETS_DROPDOWN_AVTYPE_EPISODE') }}</th>
                            <th>{{ $t('lang_PUBLISHING_USAGE_CAPTION_WORK_NUMBER') }}</th>
                            <th class="text-right">{{ $t('lang_PUBLISHING_USAGE_CAPTION_FINDING_MUSIC') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in finding.reported" :key="row.id" :class="{ 'font-weight-bold': row.sameWork }">
                            <td>{{ date(row.date) }} {{ row.startTime }}</td>
                            <td>{{ row.stationCode }}</td>
                            <td>{{ row.showTitle }}<span v-if="row.episode" class="subtitleGray--text"> · {{ row.episode }}</span></td>
                            <td>{{ row.workNumber || '–' }}</td>
                            <td class="text-right">{{ row.musicSeconds !== null ? duration(row.musicSeconds) : '–' }}</td>
                        </tr>
                    </tbody>
                </v-simple-table>
                <div v-if="finding.match.deltaSeconds !== null" class="caption mt-2" :alvqa="alvqa + '-evidence'">
                    {{ $t(verdictText) }} ·
                    {{ $t('lang_PUBLISHING_USAGE_CAPTION_FINDING_DELTA', { delta: finding.match.deltaSeconds, tolerance: finding.match.toleranceSeconds }) }}
                    <span v-if="finding.match.episodeSignal"> · {{ $t('lang_PUBLISHING_USAGE_CAPTION_EPISODE_SIGNAL_' + finding.match.episodeSignal.toUpperCase()) }}</span>
                    <span v-if="finding.match.reportedOnPreviousDay"> · {{ $t('lang_PUBLISHING_USAGE_CAPTION_FINDING_PREVIOUS_DAY') }}</span>
                </div>
            </v-col>
        </v-row>

<!-- REVIEW -->
        <v-row class="pl-4 pr-4" v-if="finding.review.reviewable">
            <v-col cols="12" md="8">
                <v-textarea
                    v-model="claimText"
                    :label="$t('lang_PUBLISHING_USAGE_CAPTION_FINDING_CLAIM_TEXT')"
                    :hint="finding.review.customClaimText ? $t('lang_PUBLISHING_USAGE_CAPTION_FINDING_CLAIM_TEXT_CUSTOM') : $t('lang_PUBLISHING_USAGE_CAPTION_FINDING_CLAIM_TEXT_GENERATED')"
                    persistent-hint
                    rows="2"
                    auto-grow
                    counter="2000"
                    :alvqa="alvqa + '-textarea-claim'"
                >
                    <template v-slot:append>
                        <BaseIcon
                            v-if="finding.review.customClaimText"
                            icon="restart_alt"
                            tooltipText="lang_PUBLISHING_USAGE_CAPTION_FINDING_CLAIM_TEXT_RESET"
                            :alvqa="alvqa + '-icon-reset-claim'"
                            @click="save({ customClaimText: null })"
                        />
                    </template>
                </v-textarea>
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center justify-end flex-wrap">
                <BaseButton
                    v-if="claimChanged"
                    label="lang_GENERAL_BUTTON_SAVE"
                    class="mr-2 mb-2"
                    :loading="saving"
                    :alvqa="alvqa + '-button-save-claim'"
                    @click="save({ customClaimText: claimText })"
                />
                <BaseButton
                    label="lang_GENERIC_CAPTION_DECLINE"
                    icon="cancel"
                    :color="finding.review.decision === 'rejected' ? 'error' : 'buttonCancel'"
                    class="mr-2 mb-2"
                    :loading="saving"
                    :alvqa="alvqa + '-button-reject'"
                    @click="decide('rejected')"
                />
                <BaseButton
                    label="lang_GENERIC_CAPTION_ACCEPT"
                    icon="check_circle"
                    :color="finding.review.decision === 'accepted' ? 'buttonNewEntity' : 'buttonStandard'"
                    class="mb-2"
                    :loading="saving"
                    :alvqa="alvqa + '-button-accept'"
                    @click="decide('accepted')"
                />
            </v-col>
        </v-row>
        <v-row class="pl-4 pr-4" v-else>
            <v-col class="body-2 subtitleGray--text" :alvqa="alvqa + '-not-reviewable'">
                {{ $t('lang_PUBLISHING_USAGE_CAPTION_FINDING_NOT_REVIEWABLE') }}
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import BaseButton from '@/components/_buttons/BaseButton'
import BaseIcon from '@/components/_buttons/BaseIcon'
import OutcomeChip from '../../_components/OutcomeChip'
import { VERDICTS } from '../../usageOptions'

export default {
    name: 'FindingReadOnly',
    components: { BaseButton, BaseIcon, OutcomeChip },
    props: {
        findingId: { type: Number, required: true }
    },
    data() {
        return {
            alvqa: 'publishing-usage-finding',
            claimText: '',
            saving: false
        }
    },
    computed: {
        finding() { return this.$store.getters.getUsageReviewDetails.find(f => f.id === this.findingId) },
        airing() { return this.finding.airing },
        verdictText() { return VERDICTS[this.finding.verdict] || this.finding.verdict },
        claimChanged() { return (this.claimText || '').trim() !== (this.finding.review.claimText || '').trim() }
    },
    watch: {
        finding: {
            immediate: true,
            handler(finding) { if (finding) this.claimText = finding.review.claimText }
        }
    },
    methods: {
        date(value) { return this.$d(new Date(value + 'T00:00:00'), 'alvDateFormat') },
        duration(seconds) {
            const m = Math.floor(seconds / 60)
            const s = seconds % 60
            return m + ':' + String(s).padStart(2, '0')
        },
        decide(decision) {
            // Clicking the decision a finding already has takes it back
            this.save({ decision: this.finding.review.decision === decision ? 'undecided' : decision })
        },
        async save(body) {
            this.saving = true
            await this.$store.dispatch('reviewUsageFinding', { findingId: this.finding.id, body })
            this.saving = false
        }
    }
}
</script>

<style scoped>
.finding-table {
    background-color: transparent !important;
}
.finding-table td:first-child {
    width: 140px;
}
</style>
